"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AgentSuccessStories() {
  const testimonials = [{
    quote: "RMA Solutions transformed our lead pipeline. Their performance-based model allowed us to scale our insurance agency without the typical upfront marketing risks.",
    author: "Michael Sterling",
    position: "Agency Principal",
    location: "Chicago, IL",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/agent_testimonial_one.webp"
  }, {
    quote: "Finally, a partner that understands the insurance business. Their team acts as an extension of our own, handling customer outreach with professional precision.",
    author: "David Rodriguez",
    position: "Insurance Broker",
    location: "Miami, FL",
    avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/agent_testimonial_three.png"
  }];
  const [active, setActive] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const interval = setInterval(() => {
      setActive(prev => (prev + 1) % testimonials.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [testimonials.length]);
  const goPrev = () => setActive(prev => (prev - 1 + testimonials.length) % testimonials.length);
  const goNext = () => setActive(prev => (prev + 1) % testimonials.length);
  return <section data-section-id="500" id="agent-success-stories" className="bg-background text-foreground py-24 md:py-32 px-6 sm:px-8 md:px-12 lg:px-16 relative overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700835/img/grid_pattern_overlay.webp)] bg-cover bg-center" />

      <div ref={ref} className={`relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {/* Left Column: Heading and Narrative Controls */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="font-mono text-xs uppercase tracking-[0.2em] font-medium mb-4 text-primary">
              § 04 / PARTNER SUCCESS
            </p>
            <h2 className="font-default font-semibold text-3xl sm:text-4xl lg:text-[44px] leading-[1.12] tracking-tight mb-6 text-foreground">
              Proven results for insurance leaders
            </h2>
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed max-w-md mb-9">
              Hear from agency principals and brokers who have scaled their operations using our performance-based lead generation and sales support systems.
            </p>

            {/* Pagination Indicators */}
            <div className="flex items-center gap-2 mb-6">
              {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => setActive(i)} variant="ghost" size="sm" aria-label={`Show testimonial ${i + 1}`} className={`h-2 rounded-lg px-0 transition-all duration-300 ${active === i ? "w-10 bg-accent hover:bg-accent/90" : "w-3 bg-muted hover:bg-muted-foreground/40"}`} />)}
            </div>

            {/* Counter */}
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {String(active + 1).padStart(2, "0")} /{" "}
              {String(testimonials.length).padStart(2, "0")}
            </p>
          </div>
        </div>

        {/* Right Column: Stacked Testimonial Cards & Controls */}
        <div className="lg:col-span-7">
          <div className="relative h-[490px] sm:h-[440px] flex items-center justify-center">
            {testimonials.map((t, i) => {
            const offset = i - active;
            const isActive = offset === 0;
            const absOffset = Math.abs(offset);
            const translateX = offset * 24;
            const translateY = absOffset * 12;
            const rotate = offset * 2.5;
            const scale = isActive ? 1 : 0.95 - absOffset * 0.03;
            const opacity = isActive ? 1 : absOffset === 1 ? 0.35 : 0;
            const z = 10 - absOffset;
            return <article key={i} data-index={i} aria-hidden={!isActive} className="absolute w-full max-w-[520px] bg-card text-card-foreground rounded-xl shadow-lg p-7 sm:p-9 transition-all duration-700 ease-out border border-border" style={{
              transform: `translate(${translateX}px, ${translateY}px) rotate(${rotate}deg) scale(${scale})`,
              opacity,
              zIndex: z,
              pointerEvents: isActive ? "auto" : "none"
            }}>
                  <Quote aria-hidden="true" className="w-10 h-10 md:w-11 md:h-11 mb-5 text-accent" strokeWidth={1.5} />

                  <p className="font-default text-lg md:text-[20px] leading-[1.6] text-card-foreground mb-8 font-normal">
                    {t.quote}
                  </p>

                  <div className="flex items-center gap-4 pt-6 border-t border-border">
                    <img src={t.avatar} data-wvc-srcset={`${t.avatar}-wvc-srcset`} data-wvc-sizes={`${t.avatar}-wvc-sizes`} alt={t.author} loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-cover shrink-0 border border-border shadow-xs" />
                    <div className="min-w-0">
                      <p className="font-default text-base font-semibold text-card-foreground tracking-tight truncate">
                        {t.author}
                      </p>
                      <p className="font-mono text-xs uppercase tracking-[0.16em] mt-1 truncate text-primary">
                        {t.position} — {t.location}
                      </p>
                    </div>
                  </div>
                </article>;
          })}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-center lg:justify-start gap-3 mt-8">
            <Button variant="outline" size="lg" onClick={goPrev} aria-label="Previous testimonial" className="rounded-lg border border-border bg-card text-foreground px-5 py-2.5 shadow-xs hover:border-accent hover:bg-accent hover:text-accent-foreground transition-all duration-200">
              <ChevronLeft className="w-4 h-4" />
              <span className="font-mono text-xs uppercase tracking-[0.16em]">
                Prev
              </span>
            </Button>
            <Button variant="outline" size="lg" onClick={goNext} aria-label="Next testimonial" className="rounded-lg border border-border bg-card text-foreground px-5 py-2.5 shadow-xs hover:border-accent hover:bg-accent hover:text-accent-foreground transition-all duration-200">
              <span className="font-mono text-xs uppercase tracking-[0.16em]">
                Next
              </span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}